import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column, SELECT_COLUMN_KEY } from "react-data-grid";
import { Checkbox, CheckboxSize } from "shared/components";
import { DataGridTable } from "shared/components/DataGridTable";
import { Lead } from "views/leads-id/types";
import { useLayout } from "providers/Layout/hooks";
import { formatDate } from "views/applications-id-report/utils";
import { LEAD_ACTIVITY_TYPE_LABELS } from "shared/app/LeadsTableView/slices/LeadsTable/constants";
import { SelectPopUp } from "shared/app/LeadsTableView/slices/LeadsTable/components/SelectPopUp";
import { Stage } from "views/settings-crm/types/stage";
import { StageSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/StageSelect";
import { AssigneeSelect } from "shared/app/LeadsTableView/slices/LeadsTable/components/AssigneeSelect";

type LeadRow = Lead & { selected?: boolean };

export const LeadsTable = ({
  data,
  stagesData,
  isLoading,
  pagination,
  perPageRows,
  viewPortHeightOffset
}: {
  data?: Lead[];
  stagesData: Stage[];
  isLoading?: boolean;
  pagination?: React.ReactNode;
  perPageRows?: number;
  viewPortHeightOffset?: number;
}) => {
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  const clearSelectedRows = () => {
    setSelectedRows(new Set());
  };

  const toggleRowSelection = (id: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  };

  const stageOptions = useMemo(() => {
    return stagesData.map((option) => ({
      label: String(option.name),
      value: Number(option.id),
      color: option.color
    }));
  }, [stagesData]);

  const columns = useMemo<Column<LeadRow>[]>(
    () => [
      {
        key: SELECT_COLUMN_KEY,
        name: "",
        width: 52,
        minWidth: 52,
        frozen: true,
        resizable: false,

        renderCell: ({ row }) => (
          <div
            className="flex items-center justify-center w-full h-full !hover:bg-red-500"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              wrapperClassName="!w-auto !flex-none !p-3 justify-center"
              size={CheckboxSize.SM}
              checked={selectedRows.has(row.id)}
              onChange={() => toggleRowSelection(row.id)}
            />
          </div>
        )
      },
      {
        key: "name",
        name: "Name",
        minWidth: 150,
        resizable: true,
        renderCell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <p className="text-content-1000 font-medium text-sm truncate">
              {row.first_name} {row.last_name}
            </p>
            <p className="text-content-400 text-xs font-normal truncate">
              {row.primary_email}
            </p>
          </div>
        )
      },
      {
        key: "property_interested",
        name: "Property interested",
        minWidth: 150,
        resizable: true,
        renderCell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <p className="text-content-1000 font-normal text-sm truncate">
              {row.property?.name || "-"}
            </p>
            <p className="text-content-400 text-xs font-normal truncate">
              {row.property?.address.one_line_address || "-"}
            </p>
          </div>
        )
      },
      {
        key: "source",
        name: "Source",
        minWidth: 200,
        resizable: true,
        renderCell: ({ row }) => (
          <p className="text-content-1000 font-medium text-sm truncate">
            {row.source?.name || "-"}
          </p>
        )
      },
      {
        key: "assigned_to",
        name: "Assigned to",
        minWidth: 240,
        resizable: true,
        renderCell: ({ row }) => {
          return (
            <AssigneeSelect
              value={row.partner_user_id ?? row.assigned_to_user?.id ?? null}
              rowId={String(row.id)}
            />
          );
        }
      },
      {
        key: "recent_activity",
        name: "Most recent activity",
        width: 270,
        minWidth: 220,
        resizable: true,
        renderCell: ({ row }) => {
          const recentActivity = row.recent_activity_summary;
          return (
            <div className="space-y-2">
              <p className="text-content-1000 font-medium text-sm truncate">
                {recentActivity
                  ? LEAD_ACTIVITY_TYPE_LABELS[recentActivity.activity_type]
                  : "-"}
              </p>

              <p className="text-content-300 text-xs font-normal">
                {recentActivity
                  ? formatDate(recentActivity.occurred_at, true)
                  : "-"}
              </p>
            </div>
          );
        }
      },
      {
        key: "stage_selector",
        name: "Current stage",
        minWidth: 240,
        resizable: true,
        renderCell: ({ row }) => {
          return (
            <StageSelect
              value={row.current_stage?.id}
              options={stageOptions}
              rowId={String(row.id)}
            />
          );
        }
      }
    ],
    [selectedRows, stageOptions]
  );

  const { sectionHeight } = useLayout();

  // Convert selectedRows Set to RowSelectionState format for SelectPopUp
  const selectedRowState = useMemo(() => {
    const state: Record<string, boolean> = {};
    selectedRows.forEach((id) => {
      state[String(id)] = true;
    });
    return state;
  }, [selectedRows]);

  return (
    <div className="relative">
      <DataGridTable
        viewPortHeight={sectionHeight}
        columns={columns}
        rows={data || []}
        loading={isLoading}
        onRowClick={(row) => navigate(`/leads/${row.id}`)}
        footer={pagination}
        perPageRows={perPageRows}
        viewPortHeightOffset={viewPortHeightOffset || 250}
        rowHeight={72}
        headerRowHeight={44}
      />
      <SelectPopUp
        selectedRow={selectedRowState}
        clearSelectedRows={clearSelectedRows}
      />
    </div>
  );
};
